/** In-memory RfqService key for Parts Library → AI create (not put on the URL). */
export const SEED_LIBRARY_PART_KEY = 'seedLibraryPart';

export type SeedLibraryFile = {
  fileName: string;
  s3Key: string;
  fileUrl?: string;
};

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
}

function pickString(source: Record<string, unknown>, keys: string[]): string {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
}

function asFileList(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function toSeedFileEntry(raw: unknown): SeedLibraryFile | null {
  if (typeof raw === 'string') {
    const fileName = raw.trim();
    if (!fileName || fileName.toLowerCase() === 'new part') {
      return null;
    }
    return { fileName, s3Key: fileName };
  }

  const source = asRecord(raw);
  const fileName = pickString(source, [
    'fileName',
    'FileName',
    's3Key',
    'S3Key',
    'completeFileName',
    'CompleteFileName',
    'primaryPartFile',
    'PrimaryPartFile'
  ]);
  if (!fileName || fileName.toLowerCase() === 'new part') {
    return null;
  }

  const fileUrl = pickString(source, ['fileUrl', 'FileUrl', 'url', 'Url']);
  return fileUrl ? { fileName, s3Key: fileName, fileUrl } : { fileName, s3Key: fileName };
}

/**
 * Widget `buildSeededFiles` only reads `partsFiles` (no merge with primary).
 * Match the library paperclip list: primary first, then every partsFiles row,
 * including a second listing of the same STEP key.
 */
export function collectSeedLibraryFiles(
  part: Record<string, unknown> | null | undefined
): SeedLibraryFile[] {
  if (!part) {
    return [];
  }

  const files: SeedLibraryFile[] = [];

  const pushVisible = (raw: unknown) => {
    const entry = toSeedFileEntry(raw);
    if (entry) {
      files.push(entry);
    }
  };

  const pushExtra = (raw: unknown) => {
    const entry = toSeedFileEntry(raw);
    if (!entry || files.some((file) => file.fileName === entry.fileName)) {
      return;
    }
    files.push(entry);
  };

  pushVisible(part['primaryPartFile'] ?? part['PrimaryPartFile']);
  asFileList(part['partsFiles'] ?? part['PartsFiles']).forEach(pushVisible);

  pushExtra(part['rfqPartFile'] ?? part['RfqPartFile']);
  asFileList(part['attachmentFileName'] ?? part['AttachmentFileName']).forEach(pushExtra);

  return files;
}

/**
 * Map a Parts Library API row into OttoRFQWidget `seedLibraryPart`.
 * Extra fields are omitted; the widget maps this object itself.
 */
export function mapSeedLibraryPart(part: Record<string, unknown> | null | undefined): Record<string, unknown> | null {
  if (!part || part['partId'] == null) {
    return null;
  }

  const seed: Record<string, unknown> = {
    partId: part['partId'],
    partName: part['partName'],
    partNumber: part['partNumber'],
    partDescription: part['partDescription'] ?? '',
    materialId: part['materialId'],
    materialName: part['materialName'],
    parentPartCategoryId: part['parentPartCategoryId'],
    parentCategoryName: part['parentCategoryName'],
    childPartCategoryId: part['childPartCategoryId'],
    childCategoryName: part['childCategoryName'],
    postProductionProcessId: part['postProductionProcessId'],
    postProductionProcessName: part['postProductionProcessName'],
    tightestToleranceId: part['tightestToleranceId'],
    partSizeUnitId: part['partSizeUnitId'],
    geometryId: part['geometryId'],
    length: part['length'],
    width: part['width'],
    height: part['height'],
    depth: part['depth'],
    diameter: part['diameter'],
    volume: part['volume'],
    surface: part['surface'],
    primaryPartFile: part['primaryPartFile'] ?? part['PrimaryPartFile']
  };

  const partsFiles = collectSeedLibraryFiles(part);
  if (partsFiles.length) {
    seed['partsFiles'] = partsFiles;
  }

  const qtyList = part['rfqPartQuantityList'];
  if (Array.isArray(qtyList) && qtyList.length) {
    const quantities = qtyList
      .map((q: { partQty?: number }) => q && q.partQty)
      .filter((n: unknown) => typeof n === 'number' && n > 0);
    if (quantities.length) {
      seed['quantities'] = quantities;
    }
  }

  return seed;
}
